import { InfoCircleOutlined } from '@ant-design/icons';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { useRequest } from 'ahooks';
import { Page, PageContent } from 'components/layout/page';
import PageTopBar from 'components/navigation/PageTopBar';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from 'components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel } from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Switch } from 'components/ui/switch';
import dayjs from 'dayjs';
import useShouldAskForPeriod from 'hooks/useShouldAskForPeriod';
import { useUpdateLicenseCode } from 'hooks/useUpdateLicenseCode';
import { cn } from 'lib/utils';
import { supportEmail } from 'locales/fr/translation';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ApiUsersManager from 'services/api/ApiUsersManager';
import { toast } from 'sonner';
import { userActions } from 'store/users';
import {
  selectCurrentUser,
  selectCurrentUserSubscription,
  selectNonRenewingSubscriptionExpiryDate,
} from 'store/users/selectors';
import Platform from 'utils/Platform';

const sectionClassName = 'divide-y divide-background rounded-xl bg-card px-2';
const lineClassName = 'flex items-center justify-between gap-2 px-2 py-3';
const lineDescriptionClassName =
  'mt-2 block text-2xs leading-normal text-subtle-foreground [&_.anticon]:mr-1 [&_.anticon]:text-foreground';

type LicenseFormValues = { licenseCode: string };

function NewLicenseForm({ defaultLicenseCode, onCancel }: { defaultLicenseCode: string; onCancel: () => void }) {
  const { t } = useTranslation();
  const form = useForm<LicenseFormValues>({ mode: 'onChange', defaultValues: { licenseCode: defaultLicenseCode } });

  const { run: updateLicenseCode } = useUpdateLicenseCode({
    onSuccess: () => {
      onCancel();
    },
    posthogSource: 'settings',
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(updateLicenseCode)} className="flex w-full flex-col gap-6 px-2 pt-2 pb-4">
        <FormField
          control={form.control}
          name="licenseCode"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center gap-2">
              <FormLabel className="shrink-0">{`${t('settings.license.newLicense')} :`}</FormLabel>
              <FormControl>
                <Input {...field} type="text" placeholder={t('settings.license.placeholder')} />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="flex justify-center gap-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            {t('settings.cancel')}
          </Button>
          <Button type="submit">{t('settings.license.saveLicense')}</Button>
        </div>
      </form>
    </Form>
  );
}

export default function SettingsPage() {
  // Hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const [isChangingLicense, setIsChangingLicense] = useState(false);
  const currentUserSubscription = useSelector(selectCurrentUserSubscription);
  const [isStripeModalOpen, setIsStripeModalOpen] = useState(false);
  const nonRenewingSubscriptionExpiryDate = useSelector(selectNonRenewingSubscriptionExpiryDate);

  useRequest(() => Purchases.getCustomerInfo(), {
    manual: !Platform.isNative(),
    onSuccess: data => {
      dispatch(userActions.setCurrentUserSubscription(data.customerInfo));
    },
  });

  const { run: restorePurchases } = useRequest(() => Purchases.restorePurchases(), {
    manual: true,
    onSuccess: data => {
      dispatch(userActions.setCurrentUserSubscription(data.customerInfo));
      toast.success(t('settings.subscription.restorePurchasesSuccess'));
    },
    onError: error => {
      toast.error(t('settings.subscription.restorePurchasesError'));
      console.error(error);
    },
  });

  const { isPeriodTrackingEnabled, setShouldAskForPeriod, loadingShouldAskForPeriod } = useShouldAskForPeriod();

  const { run: storeConsentsToEmailNotifications, loading: loadingEmailConsents } = useRequest(
    consentsToEmailNotifications => ApiUsersManager.updateConsentsToEmailNotifications(consentsToEmailNotifications),
    {
      manual: true,
      onSuccess: data => {
        dispatch(userActions.setEmailConsents(data.consents_to_email_notifications));
      },
    },
  );

  const activeSubscription = currentUserSubscription?.activeSubscriptions[0];
  const licenseCodeExpiryDate = dayjs(currentUser?.license_code_expiry_date);
  const hasLicenseExpired = licenseCodeExpiryDate.isBefore(dayjs());

  return (
    <Page>
      <PageTopBar title={t('menu.settings')} />
      <PageContent>
        <section className="flex flex-col gap-2">
          <h4>{t('settings.chatsSection')}</h4>
          <div className={sectionClassName}>
            <div className={lineClassName} data-testid="settings.email">
              <p>
                {t('settings.chats.emailNotifications')}
                {Capacitor.getPlatform() === 'web' && (
                  <>
                    <br />
                    <span className={lineDescriptionClassName}>
                      <InfoCircleOutlined />
                      {t('settings.chats.emailNotificationsDescription')}
                    </span>
                  </>
                )}
              </p>
              <Switch
                checked={currentUser?.consents_to_email_notifications}
                disabled={loadingEmailConsents}
                onCheckedChange={storeConsentsToEmailNotifications}
              />
            </div>
          </div>
        </section>
        <section className="flex flex-col gap-2">
          <h4>{t('menu.tracking')}</h4>
          <div className={sectionClassName}>
            <div className={lineClassName} data-testid="settings.period">
              <p>{t('settings.periodQuestions')}</p>
              <Switch
                checked={isPeriodTrackingEnabled}
                onCheckedChange={setShouldAskForPeriod}
                disabled={loadingShouldAskForPeriod}
              />
            </div>
          </div>
        </section>
        <section className="flex flex-col gap-2">
          <h4>{t('settings.accountSection')}</h4>
          <div className={sectionClassName}>
            {!isChangingLicense ? (
              <div className={lineClassName}>
                <p>
                  {currentUser?.license_code ? (
                    <>
                      {t('settings.license.currentLicense')}:{' '}
                      <span className="font-bold uppercase">{currentUser?.license_code}</span>
                    </>
                  ) : (
                    <span className="text-subtle-foreground">{t('settings.license.noLicense')}</span>
                  )}
                  {licenseCodeExpiryDate.isValid() && (
                    <span
                      className={cn(
                        lineDescriptionClassName,
                        hasLicenseExpired ? 'text-destructive-text' : 'text-warning-text',
                      )}
                    >
                      {hasLicenseExpired ? (
                        <span>{t('settings.license.expired', { date: licenseCodeExpiryDate.format('LL') })}</span>
                      ) : (
                        <span>{t('settings.license.expiresOn', { date: licenseCodeExpiryDate.format('LL') })}</span>
                      )}
                    </span>
                  )}
                </p>
                <Button variant="link" onClick={() => setIsChangingLicense(true)}>
                  {t('settings.license.changeLicense')}
                </Button>
              </div>
            ) : (
              <div>
                <NewLicenseForm
                  defaultLicenseCode={(currentUser?.license_code || '').toUpperCase()}
                  onCancel={() => setIsChangingLicense(false)}
                />
              </div>
            )}
            <div className={lineClassName}>
              <p>
                {activeSubscription ? (
                  t('settings.subscription.subscriptionInProgress')
                ) : nonRenewingSubscriptionExpiryDate ? (
                  t('settings.subscription.nonRenewingSubscription', {
                    date: nonRenewingSubscriptionExpiryDate.format('LL'),
                  })
                ) : (
                  <span className="text-subtle-foreground">{t('settings.subscription.noSubscription')}</span>
                )}
                <br />
                {Platform.isNative() && (
                  <Button variant="link" onClick={restorePurchases}>
                    {t('settings.subscription.restorePurchases')}
                  </Button>
                )}
              </p>
              {Platform.isNative() && activeSubscription && !activeSubscription.startsWith('prod_') && (
                <Button asChild variant="link">
                  <Link
                    to={
                      {
                        ios: 'https://apps.apple.com/account/subscriptions',
                        android: 'https://play.google.com/store/account/subscriptions',
                      }[Platform.getPlatform()] || ''
                    }
                    onClick={() => {
                      posthog.capture('settings.subscription.manage_clicked');
                    }}
                  >
                    {t('settings.subscription.manage')}
                  </Link>
                </Button>
              )}
              {activeSubscription && activeSubscription.startsWith('prod_') && (
                // These are the very first stripe subscriptions, cancelling goes through email
                <Button variant="link" onClick={() => setIsStripeModalOpen(true)}>
                  {t('settings.subscription.manage')}
                </Button>
              )}
              <Dialog open={isStripeModalOpen} onOpenChange={setIsStripeModalOpen}>
                <DialogContent>
                  <DialogTitle className="sr-only">{t('settings.subscription.manage')}</DialogTitle>
                  <DialogDescription asChild>
                    <p className="text-base text-foreground">
                      <Trans
                        i18nKey="settings.subscription.manageStripeSubscription"
                        components={{ a: <Link to={`mailto:${supportEmail}`} />, br: <br /> }}
                      />
                    </p>
                  </DialogDescription>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsStripeModalOpen(false)}>
                      {t('settings.cancel')}
                    </Button>
                    <Button onClick={() => setIsStripeModalOpen(false)}>{t('global.buttons.ok')}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </section>
        {import.meta.env.MODE !== 'production' && (
          // Dev and staging only: forgets every preference, as on a fresh install.
          <section className="flex flex-col gap-2">
            <h4>Dev</h4>
            <div className={sectionClassName}>
              <div className={lineClassName}>
                <p>
                  Préférences
                  <span className={lineDescriptionClassName}>Réinitialiser les préférences locales</span>
                </p>
                <Button
                  variant="destructive-subtle"
                  onClick={async () => {
                    await Promise.all(Object.values(Keys).map(key => Preferences.remove({ key })));
                    window.location.reload();
                  }}
                >
                  Réinitialiser
                </Button>
              </div>
            </div>
          </section>
        )}
        <p className="mt-2 text-center text-xs text-subtle-foreground">
          <Trans
            i18nKey="settings.supportParagraph"
            components={{ a: <Link to={`mailto:${supportEmail}`} />, br: <br /> }}
          />
        </p>
      </PageContent>
    </Page>
  );
}
